import { useTranslations } from "next-intl"
import "@/styles/home.css"
import { ArrowDownIcon, MapPin, Trophy, UserPlus, Users } from "lucide-react"
import { Button } from "@/components/shared/ui/button"
import { useRouter } from "@/navigation"
import ScrollSlideRight from "@/components/shared/animations/ScrollSlideRight"
import { usePricesStore } from "@/store/pricesStore"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay, EffectFade } from "swiper/modules"
import "swiper/css"
import "swiper/css/effect-fade"
import Image from "next/image"
import { useEffect, useState } from "react"
import CountUp from "@/components/shared/ui/CountUp"

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(true); // Default to true for SSR
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    return isMobile;
};

const Hero = () => {
    const t = useTranslations('Home')
    const router = useRouter()
    const { homeAnalytics } = usePricesStore()
    const [mounted, setMounted] = useState(false)
    const isMobile = useIsMobile()

    // Only render Swiper after component mounts
    useEffect(() => {
        setMounted(true)
    }, [])

    // Use a single, responsive className for the text column to avoid hydration diffs
    const contentClasses = "relative z-20 overflow-visible flex flex-col gap-8 h-full py-16 px-6 md:py-24 md:px-20 md:rounded-bl-[230px] bg-gradient-to-b from-primary/5 via-primary/10 to-primary/5 md:bg-gradient-to-br md:from-sub_primary/80 md:via-sub_primary/95 md:to-sub_primary/80 md:backdrop-blur-md md:bg-white/5 md:border-r md:border-t md:border-white/10 md:shadow-[0_0_50px_rgba(255,255,255,0.1)]"
    
    const statCardClasses = isMobile
        ? "flex flex-col items-start p-4 rounded-xl backdrop-blur-md bg-white/80 hover:bg-white/90 transition-all duration-300 shadow-lg hover:shadow-xl border border-white/50"
        : "flex flex-col items-start p-4 rounded-xl backdrop-blur-sm bg-white/30 hover:bg-white/40 transition-all duration-300"

    const iconAnimations = [
        'animate-bounce-slow',
        'animate-pulse-slow',
        'animate-wiggle',
        'animate-float'
    ];

    return (
        <div className="relative min-h-screen w-full overflow-hidden">
            {/* Content section - Render first on mobile for better performance */}
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 min-h-screen">
                <div className={contentClasses}>
                    <div className={isMobile ? '' : 'animate-fade-in'}>
                        <h1 className={`main-head drop-shadow-sm w-full max-w-full break-words pr-2 md:pr-4 ${
                            isMobile 
                                ? 'text-4xl font-extrabold text-center bg-clip-text text-transparent bg-gradient-to-r from-primary via-primary to-secondary' 
                                : ''
                        }`}>
                            {t('title')}
                        </h1>
                    </div>

                    <div className={isMobile ? '' : 'animate-fade-in delay-200'}>
                        <p className={`font-medium ${
                            isMobile 
                                ? 'text-lg text-center px-4 text-gray-600' 
                                : 'text-xs w-full md:text-xl md:w-3/4 text-[#888888]'
                        }`}>
                            {t('subTitle')}
                        </p>
                    </div>

                    {/* Stats Grid */}
                    <div className={`grid gap-4 w-full ${
                        isMobile 
                            ? 'grid-cols-2 mt-6' 
                            : 'grid-cols-2 md:grid-cols-4 animate-fade-in delay-300'
                    }`}>
                        {[
                            { icon: MapPin, value: homeAnalytics?.countries, label: 'row.city' },
                            { icon: Trophy, value: homeAnalytics?.successes, label: 'row.client' },
                            { icon: Users, value: homeAnalytics?.trainers, label: 'row.trainer' },
                            { icon: UserPlus, value: homeAnalytics?.users, label: 'row.newLogins' }
                        ].map((item, index) => (
                            <div key={index} className={statCardClasses}>
                                <div className="flex gap-2 items-center">
                                    <div className={`${iconAnimations[index]} p-2 rounded-full bg-primary/10`}>
                                        <item.icon className={`${isMobile ? 'w-5 h-5' : 'w-5 h-5'} text-primary`} />
                                    </div>
                                    <CountUp
                                        end={Number(item.value ?? 0)}
                                        duration={1400}
                                        className={`font-bold ${isMobile ? 'text-2xl text-primary' : 'text-2xl'}`}
                                    />
                                </div>
                                <hr className="separator opacity-20 w-full my-2" />
                                <p className={`font-medium ${
                                    isMobile 
                                        ? 'text-sm w-full text-gray-600' 
                                        : 'text-xs w-full md:w-3/4 text-[#888888]'
                                }`}>
                                    {t(item.label)}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* CTA Button */}
                    <div className={isMobile ? '' : 'animate-fade-in delay-400'}>
                        <Button 
                            onClick={() => router.push("/#services")} 
                            variant="secondary" 
                            className={`rounded-full hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-r from-primary to-secondary ${
                                isMobile 
                                    ? 'mt-10 w-full py-7 text-lg font-bold' 
                                    : 'mt-16 md:mt-10 w-1/2 py-6'
                            }`}
                        >
                            <span className="text-white">{t('getStarted')}</span>
                            <ArrowDownIcon className={isMobile ? 'w-5 h-5 animate-bounce text-white' : 'w-5 h-5 text-white'} />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Background Swiper - Only render on desktop and after mount */}
            {!isMobile && mounted && (() => {
                const backgroundImages = [
                    {
                        src: '/hero/bg1.webp',
                        alt: 'Hero Background 1',
                        width: 1440,
                        height: 800,
                        blurDataURL: "data:image/webp;base64,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"
                    },
                    {
                        src: '/hero/bg2.webp',
                        alt: 'Hero Background 2',
                        width: 1440,
                        height: 800,
                        blurDataURL: "data:image/webp;base64,UklGRqAGAABXRUJQVlA4WAoAAAAgAAAAiQIAsQEASUNDUMgBAAAAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADZWUDggsgQAAJBYAJ0BKooCsgE+7XawVqmnJCOgCJEwHYlpbuFkitNgEQKMJ8+//4z0Cdh3XjH6AE+4VWmxemd4rMzDqsyG6eh70xP06YzvFMCNkXs9mZyDH/CotMdFZJDso96PyQRRfoUD+fduZw8Pxe0tq0aez3GxuTbiOxntT6jxFDdSEEAnO+MRql7SY+c4IsjzxBnlJeG3pGAa4WO6IbLAJ2LwgD8y4n37zTuAxhl6Rajtzf5b6qh/aIbT4/1BH7n2jBseJulB1u0295ZEiKiJytN+zpGnUIA8ASXaLx98u0liPsjzbbG26c+xvWnPTwuPnJauZ8ku/bBbP9c+MHU5xpKVSsREkrrre6/yA+bKq5nySWI+yPMeowDUncEmWAEa1BBHkO4Gq1cz5Jd+16f658YOt3AZ209HarXECGGmGawMOgYpq4RyTL2AYwT7pQdXiZk+2v8ZpBiKbbRIoit37XtF5bbjxgnslPpm/FaH/2NpkAmzf2t3o3tFHhijp3AYwTZ7VXL6uF81JYjj7ns+lyGKLAP7+keY88kBi5X+9Q5mTJni85z6giPmAF4RyTaAtYvLsemybQzp7dTvFZmYvSjZR/0hUkGdz6gCLQfzfiszMXpniVNJWfnuPbW3R+clDW2zMxemd4rM1XdKWmZhLtMHJvDu8Jl7TvFZmYvWT+F12pyMctFNOybfgJemd4rMzF6Z6JW6/Tzhv5tzn/YRjy9p3iszMXppajmZKmWVZQvmhgcvYgnmZi9M7xWZm/0XL0Om177pDLgp6c+XtO8VmZi9M74xuf18R7zkHCkSonSZe07xWZmL0zvkGO5cpO6KFSMRuw4asFzjMy9p3iszOC6iVr8rbySu1BJdHeKzMxemd4rNncEC34dzpb8nSBt6fFZmYvTO8Vm4TS/+0pldZOfDzV1sd4rMzF6Z3imwo62ZrgTiriX0zUwr0zvFZmYcAAD+9+9k15wVPJvNBLN8uQKPTjp55i2OFcInbMzYNkJ0DiwtRrbJsC02Eys4MCp06GO8jkbbHN660UcCbL1vSK6YjlCSP0CZJ30CUQZio7lzAjbJI4EZuLT2ZC4zcR1PfnyGgTeBL1BN20UDbqjb0IuU14bnQ++qvRQ+un4PajgB2gnlgDh7WqAojLajJMZ8uGBS1j2T3pxjG4p7xtbXCKnd9Tx6MvqmtEskJDtQCea9u1yuwG7KGVuT+GFI2+8HyY1T3rZ7Vi/fZEaIpstlAf/7pyNDMQa8HR3SnSdqwCfITQ4scFIrg5HnZg7GuL7mNs8NwE0eZIXn2ZaOWCa2WwxcVNfzl62KPiTPB8YHdEA60/TxjkzQNot7B5xaGR2UphjESw+B3MA9JCXGjOm1uY2Yu5nLzupJaXbu0pdQgyJdSKN+ZPBMQEj7v61uW3BAJfAIlXhA2/ymYfqvvo9MEDIRUoS1B66bMTqKRsaIB6w9kTVTvPYSsWWT/FAAlcMcjs0UQBiSuGMT2UYAABJzypmNr9LmiggANBgITuMeK8hTVQTAASLAT1yFmHOQQIABNyLGQqLi15J7LbrxAGyVjHe0tpJ6FiPfgAdReu3B2GPLWiwQFS+mn5Sj/xsFu/zGfUQAAAAA"
                    },
                ];

                return (
                    <>
                        <div className="absolute inset-0 z-0">
                            <Swiper
                                modules={[Autoplay, EffectFade]}
                                effect="fade"
                                autoplay={{
                                    delay: 8000,
                                    disableOnInteraction: false,
                                }}
                                loop={true}
                                speed={1500}
                                className="w-full h-full"
                            >
                                {backgroundImages.map((image, index) => (
                                    <SwiperSlide key={index} className="w-full h-full">
                                        <div className="relative w-full h-full">
                                            <Image
                                                src={image.src}
                                                alt={image.alt}
                                                fill
                                                priority={index === 0}
                                                quality={75}
                                                sizes="(max-width: 768px) 100vw, (max-width: 1440px) 50vw"
                                                className="object-cover"
                                                style={{
                                                    objectFit: 'cover',
                                                    objectPosition: 'center',
                                                }}
                                                placeholder="blur"
                                                blurDataURL={image.blurDataURL}
                                            />
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                        <div className="absolute inset-0 z-5 bg-gradient-to-r from-black/30 to-transparent" />
                    </>
                );
            })()}

            {/* Remove animations on mobile */}
            {!isMobile && (
                <style jsx global>{`
                    @keyframes zoom {
                        from { transform: scale(1); }
                        to { transform: scale(1.1); }
                    }
                    .animate-zoom {
                        animation: zoom 10s infinite alternate;
                    }

                    @keyframes bounce-slow {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-10px); }
                    }
                    .animate-bounce-slow {
                        animation: bounce-slow 3s infinite ease-in-out;
                    }

                    @keyframes pulse-slow {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.1); }
                    }
                    .animate-pulse-slow {
                        animation: pulse-slow 3s infinite ease-in-out;
                    }

                    @keyframes wiggle {
                        0%, 100% { transform: rotate(0deg); }
                        25% { transform: rotate(-10deg); }
                        75% { transform: rotate(10deg); }
                    }
                    .animate-wiggle {
                        animation: wiggle 3s infinite ease-in-out;
                    }

                    @keyframes float {
                        0%, 100% { transform: translateY(0) rotate(0deg); }
                        25% { transform: translateY(-5px) rotate(-5deg); }
                        75% { transform: translateY(5px) rotate(5deg); }
                    }
                    .animate-float {
                        animation: float 3s infinite ease-in-out;
                    }
                `}</style>
            )}
        </div>
    );
};

export default Hero;